/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import {
  useEffect,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
  useMemo,
} from 'react'
import { useIntl } from 'react-intl'

import { makeAssociationsDiff } from '@modules/network-security-lib/makeAssociationsDiff'
import { addToast } from '@modules/cui/Toasts'
import { wrapAsAsyncRequestError } from '@modules/query/wrapAsAsyncRequestError'
import {
  useUpdateServerlessTrafficFilterMutation,
  useListAssociableProjects,
} from '@modules/network-security-lib/hooks/serverless'
import {
  usePatchElasticsearchProjectMutation,
  usePatchObservabilityProjectMutation,
  usePatchSecurityProjectMutation,
} from '@modules/project-user-lib/hooks/update'
import type {
  PatchTrafficFilterRequest,
  TrafficFilterInfo,
} from '@modules/network-user-api/v1/types'
import type { AnyUserPatchProjectRequest } from '@modules/project-user-api/types'
import { makeServerlessUpsertRequest } from '@modules/network-security-lib/makeServerlessUpsertRequest'

import type { ForwardRefRenderFunction } from 'react'
import type { TrafficFilterHandlerChildProps, TrafficFilterHandlerProps } from '../types'

/**
 * Handles the logic for serverless traffic filters.
 *
 * Exposes `save()`, `isSaving`, and `error` to the parent via a ref, and passes
 * getters and setters for the serverless traffic filter fields to the child.
 * These contracts are shared with HostedTrafficFilterHandler.
 *
 * Quite a lot going on here, out of necessity, so broken up into sections.
 * Unlike hosted traffic filters, serverless traffic filters' associations
 * belong to the projects. This makes the associations from the project context
 * simple -- an update like any other -- but from the ruleset context complex,
 * as we need to load all projects to even _display_ associations (much less
 * edit them).
 */
const ServerlessTrafficFilterUpdateHandler: ForwardRefRenderFunction<
  {
    save: () => void
  },
  TrafficFilterHandlerProps<PatchTrafficFilterRequest, TrafficFilterInfo>
> = ({ onClose, ruleset: rulesetUnderEdit, onRulesetChange, onFormStateChange, children }, ref) => {
  const intl = useIntl()

  // Initialize state for ruleset

  const [ruleset, setRuleset] = useState<PatchTrafficFilterRequest>(
    makeServerlessUpsertRequest(rulesetUnderEdit),
  )

  const isSubmittable = useMemo(() => Boolean(ruleset.name), [ruleset])

  // Initialize state for associations

  const { projects, isReady } = useListAssociableProjects()

  const [associations, setAssociations] = useState<string[]>([])

  const associationsUnderEdit: string[] = useMemo(() => {
    if (!isReady) {
      return []
    }

    return projects
      .filter((project) => project.traffic_filters?.some(({ id }) => id === rulesetUnderEdit.id))
      .map((project) => project.id)
  }, [isReady])

  useEffect(() => {
    setAssociations(associationsUnderEdit)
  }, [associationsUnderEdit])

  // State changes

  const setRulesetField = useCallback(function <
    TKey extends Exclude<keyof PatchTrafficFilterRequest, 'id'>,
    TValue extends PatchTrafficFilterRequest[TKey],
  >(key: TKey, value: TValue) {
    setRuleset((currentRuleset: PatchTrafficFilterRequest) => ({
      ...currentRuleset,
      [key]: value,
    }))
  }, [])

  // Saving

  const {
    mutate: updateServerlessTrafficFilter,
    isLoading: isUpdatingRuleset,
    error: updateError,
  } = useUpdateServerlessTrafficFilterMutation()

  const { mutateAsync: patchElasticsearchProjectMutation } = usePatchElasticsearchProjectMutation()
  const { mutateAsync: patchObservabilityProjectMutation } = usePatchObservabilityProjectMutation()
  const { mutateAsync: patchSecurityProjectMutation } = usePatchSecurityProjectMutation()

  const associationPromises = useRef<Array<Promise<unknown>>>([])

  const isSaving = isUpdatingRuleset || associationPromises.current.length > 0

  const error = wrapAsAsyncRequestError(updateError)

  const save = () => {
    updateServerlessTrafficFilter(
      { id: rulesetUnderEdit.id, body: ruleset },
      { onSuccess: saveAssociations },
    )

    // The API only handles a single add or remove at a time, so we need to batch these after the main save.
    function saveAssociations({ id: rulesetId }: { id: string }) {
      const { add, remove } = makeAssociationsDiff(associationsUnderEdit, associations)

      add.forEach((projectId) => {
        const project = projects.find((item) => item.id === projectId)

        if (!project) {
          return // sanity
        }

        const diff: Pick<AnyUserPatchProjectRequest, 'traffic_filters'> = {
          traffic_filters: [...(project.traffic_filters ?? []), { id: rulesetId }],
        }

        const patchProjectMutate = {
          elasticsearch: patchElasticsearchProjectMutation,
          observability: patchObservabilityProjectMutation,
          security: patchSecurityProjectMutation,
        }[project.type]

        patchProjectMutate({ id: projectId, body: diff })
      })

      remove.forEach((projectId) => {
        const project = projects.find((item) => item.id === projectId)

        if (!project) {
          return // sanity
        }

        const diff: Pick<AnyUserPatchProjectRequest, 'traffic_filters'> = {
          traffic_filters: (project.traffic_filters ?? []).filter(({ id }) => id !== rulesetId),
        }

        const patchProjectMutate = {
          elasticsearch: patchElasticsearchProjectMutation,
          observability: patchObservabilityProjectMutation,
          security: patchSecurityProjectMutation,
        }[project.type]

        patchProjectMutate({ id: projectId, body: diff })
      })

      // Warn but move on if some associations fail to save.
      Promise.allSettled(associationPromises.current).then((results) => {
        const hasRejected = results.some((result) => result.status === 'rejected')

        if (hasRejected) {
          addToast({
            title: intl.formatMessage({
              id: 'serverlessTrafficFilter.associations.error.title',
              defaultMessage: 'Warning',
            }),
            color: 'warning',
            iconType: 'warning',
            text: intl.formatMessage({
              id: 'serverlessTrafficFilter.associations.error.description',
              defaultMessage: 'Some project associations may not have been saved successfully.',
            }),
          })
        }

        onClose()
      })
    }
  }

  // Parent API

  useImperativeHandle(ref, () => ({
    save,
  }))

  useEffect(() => {
    onRulesetChange({ isSubmittable })
  }, [isSubmittable])

  useEffect(() => {
    onFormStateChange({ isSaving, error })
  }, [isSaving, error])

  // Rendering

  const childProps: TrafficFilterHandlerChildProps = {
    formMode: 'update',
    resourceType: 'serverless',
    name: ruleset.name,
    setName: (value) => setRulesetField('name', value),
    description: ruleset.description,
    setDescription: (value) => setRulesetField('description', value),
    includeByDefault: ruleset.include_by_default ?? false,
    setIncludeByDefault: (value) => setRulesetField('include_by_default', value),
    rules: ruleset.rules,
    setRules: (value) => setRulesetField('rules', value),
    associations,
    setAssociations,

    // read-only fields that aren't present on the update request object
    type: rulesetUnderEdit.type,
    setType: undefined,
    region: rulesetUnderEdit.region,
    setRegion: undefined,
  }

  return children(childProps)
}

export default forwardRef(ServerlessTrafficFilterUpdateHandler)
